import * as THREE from 'three';
import type { Renderer } from '@/rendering/Renderer';
import { DEPTH_NEAR_IS_BRIGHT } from './CaptureEncoding';
import type { ImageSize } from './ImageSpec';
import { ID_MAP_BACKGROUND } from './SpeciesPalette';

/**
 * The three render passes behind an enhanced render.
 *
 * A single screenshot cannot tell an image model that the blob on the left is a red maple. So the
 * same frame is rendered three ways and all three are sent:
 *
 * | Pass    | What it carries                                                        |
 * | ------- | ---------------------------------------------------------------------- |
 * | beauty  | the composition — what the user framed                                  |
 * | ID map  | which pixels belong to which species, as flat key colours               |
 * | depth   | how far away everything is, for atmospheric perspective and focus       |
 *
 * All three must be pixel-aligned and identically sized, so they share one camera and one size.
 * Each pass mutates the scene, renders, and restores it in a `finally` — an exception mid-capture
 * must never leave the editor showing flat magenta plants.
 */

export interface PassContext {
  renderer: Renderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  size: ImageSize;
  /** Editor-only geometry (grid, north arrow, selection halos, placement ghost). */
  overlayRoot: THREE.Object3D;
  floraRoot: THREE.Object3D;
  /** Buildings and paving. Painted into the ID map alongside plants. */
  siteRoot: THREE.Object3D;
  terrainRoot: THREE.Object3D;
}

/**
 * The narrow slice of `THREE.Mesh` this module touches. `instanceof THREE.Mesh` widens to
 * `Mesh<any, any, any>`, which loses type safety on the material swap; this keeps it precise.
 */
interface MeshLike extends THREE.Object3D {
  material: THREE.Material | THREE.Material[];
}

function isMesh(object: THREE.Object3D): object is MeshLike {
  return (object as Partial<THREE.Mesh>).isMesh === true;
}

export class CaptureUnsupportedError extends Error {
  constructor() {
    super('This renderer cannot capture frames — a WebGL renderer is required.');
    this.name = 'CaptureUnsupportedError';
  }
}

function renderPixels(context: PassContext, antialias?: boolean): Uint8Array {
  const capture = context.renderer.renderToPixels?.bind(context.renderer);
  if (!capture) throw new CaptureUnsupportedError();
  return capture(context.scene, context.camera, {
    width: context.size.width,
    height: context.size.height,
    ...(antialias === undefined ? {} : { antialias }),
  });
}

/**
 * The frame as the user composed it, minus editor furniture. Grid lines and selection halos are
 * design aids, not part of the garden — leaving them in teaches the model to paint them back.
 */
export function captureBeauty(context: PassContext): Uint8Array {
  const overlayVisible = context.overlayRoot.visible;
  context.overlayRoot.visible = false;
  try {
    return renderPixels(context);
  } finally {
    context.overlayRoot.visible = overlayVisible;
  }
}

/**
 * The semantic map: every plant *and every building* painted a flat, unlit key colour, everything
 * else black.
 *
 * `MeshBasicMaterial` is used deliberately — it ignores lights entirely, so a plant in deep shade
 * still reports its exact key colour. Any shading at all would blur the boundary between two species
 * and defeat the legend. Fog and tone mapping are suspended for the same reason.
 *
 * Multisampling is switched off for the same reason: an antialiased silhouette edge averages a key
 * colour with its neighbour, producing pixels that decode to no species at all. A hard, aliased edge
 * is the correct output for a semantic map.
 *
 * Site objects share the plants' palette and are keyed individually, so the legend can bind one
 * silhouette to "the 5.5 m gambrel shed" rather than leaving the model to guess what the grey box
 * in the beauty render was meant to be.
 */
export function captureIdMap(
  context: PassContext,
  keyColors: ReadonlyMap<string, string>,
  /** Resolves the palette key for a view object — a species id, or a namespaced site-object id. */
  keyIdFor: (object: THREE.Object3D) => string | undefined,
): Uint8Array {
  const overlayVisible = context.overlayRoot.visible;
  const terrainVisible = context.terrainRoot.visible;
  const background = context.scene.background;
  const fog = context.scene.fog;

  // Materials are created per key colour, not per mesh — a drift of 30 grasses shares one.
  const keyMaterials = new Map<string, THREE.MeshBasicMaterial>();
  const swapped: { mesh: MeshLike; material: THREE.Material | THREE.Material[] }[] = [];

  const materialFor = (hex: string): THREE.MeshBasicMaterial => {
    const existing = keyMaterials.get(hex);
    if (existing) return existing;
    const material = new THREE.MeshBasicMaterial({
      color: new THREE.Color(hex),
      toneMapped: false,
      fog: false,
    });
    keyMaterials.set(hex, material);
    return material;
  };

  try {
    context.overlayRoot.visible = false;
    // Terrain and sky are both "not a plant"; black is reserved for exactly that.
    context.terrainRoot.visible = false;
    context.scene.background = new THREE.Color(ID_MAP_BACKGROUND);
    context.scene.fog = null;

    for (const root of [context.floraRoot, context.siteRoot]) {
      root.traverse((node) => {
        if (!node.visible || !isMesh(node)) return;
        const keyId = keyIdFor(node);
        if (keyId === undefined) return;
        const hex = keyColors.get(keyId);
        if (hex === undefined) return;
        swapped.push({ mesh: node, material: node.material });
        node.material = materialFor(hex);
      });
    }

    return renderPixels(context, false);
  } finally {
    for (const entry of swapped) entry.mesh.material = entry.material;
    for (const material of keyMaterials.values()) material.dispose();
    context.overlayRoot.visible = overlayVisible;
    context.terrainRoot.visible = terrainVisible;
    context.scene.background = background;
    context.scene.fog = fog;
  }
}

export interface DepthPassOptions {
  /** World-space near/far to spread the gradient across. Defaults to the camera's own range. */
  nearMeters?: number;
  farMeters?: number;
}

/**
 * The depth map. `scene.overrideMaterial` swaps every material in one assignment, which is both
 * faster and safer than walking the graph.
 *
 * The camera's near/far planes are temporarily tightened to the content's actual extent. A default
 * frustum of 0.1–2000 m packs a 40 m garden into the first 2% of the depth range, producing an image
 * that is uniformly flat and useless.
 *
 * `MeshDepthMaterial` writes **near as bright and far as dark** ({@link DEPTH_NEAR_IS_BRIGHT}), so
 * the background is cleared to black: sky is effectively infinitely far away, and clearing it white
 * would tell the model the sky is the closest thing in the frame. `PromptBuilder` describes the same
 * polarity — the two must not drift apart.
 */
export function captureDepth(context: PassContext, options: DepthPassOptions = {}): Uint8Array {
  const overlayVisible = context.overlayRoot.visible;
  const background = context.scene.background;
  const fog = context.scene.fog;
  const previousOverride = context.scene.overrideMaterial;
  const depthMaterial = new THREE.MeshDepthMaterial();

  const perspective =
    context.camera instanceof THREE.PerspectiveCamera ||
    context.camera instanceof THREE.OrthographicCamera
      ? context.camera
      : null;
  const previousNear = perspective?.near;
  const previousFar = perspective?.far;

  try {
    context.overlayRoot.visible = false;
    // Sky is infinitely distant: clear to whichever end of the ramp means "far".
    context.scene.background = new THREE.Color(DEPTH_NEAR_IS_BRIGHT ? 0x000000 : 0xffffff);
    context.scene.fog = null;
    context.scene.overrideMaterial = depthMaterial;

    if (perspective && options.nearMeters !== undefined && options.farMeters !== undefined) {
      perspective.near = Math.max(0.05, options.nearMeters);
      perspective.far = Math.max(perspective.near + 1, options.farMeters);
      perspective.updateProjectionMatrix();
    }

    return renderPixels(context);
  } finally {
    context.scene.overrideMaterial = previousOverride;
    depthMaterial.dispose();
    context.overlayRoot.visible = overlayVisible;
    context.scene.background = background;
    context.scene.fog = fog;
    if (perspective && previousNear !== undefined && previousFar !== undefined) {
      perspective.near = previousNear;
      perspective.far = previousFar;
      perspective.updateProjectionMatrix();
    }
  }
}

/**
 * Distance from the camera to the nearest and furthest corners of a bounding box, used to fit the
 * depth pass's frustum to the content actually in view.
 */
export function depthRangeForBox(
  camera: THREE.Camera,
  box: THREE.Box3,
): { nearMeters: number; farMeters: number } {
  const origin = camera.getWorldPosition(new THREE.Vector3());
  let near = Infinity;
  let far = 0;
  const corner = new THREE.Vector3();
  for (let i = 0; i < 8; i += 1) {
    corner.set(
      i & 1 ? box.max.x : box.min.x,
      i & 2 ? box.max.y : box.min.y,
      i & 4 ? box.max.z : box.min.z,
    );
    const distance = origin.distanceTo(corner);
    near = Math.min(near, distance);
    far = Math.max(far, distance);
  }
  // Pad slightly so geometry exactly on the boundary is not clipped away.
  return {
    nearMeters: Math.max(0.05, near * 0.9),
    farMeters: Math.max(near + 1, far * 1.1),
  };
}

/**
 * Convert an RGBA pixel buffer from `renderToPixels` into a PNG blob.
 *
 * WebGL hands back rows bottom-first; every image consumer expects top-first, so the rows are
 * reversed during the copy rather than by flipping the canvas afterwards.
 */
export async function pixelsToPngBlob(
  pixels: Uint8Array,
  width: number,
  height: number,
): Promise<Blob> {
  const rowBytes = width * 4;
  const flipped = new Uint8ClampedArray(pixels.length);
  for (let y = 0; y < height; y += 1) {
    const source = (height - 1 - y) * rowBytes;
    flipped.set(pixels.subarray(source, source + rowBytes), y * rowBytes);
  }

  const image = new ImageData(flipped, width, height);
  const canvas = createCanvas(width, height);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not acquire a 2D context to encode the capture.');
  context.putImageData(image, 0, 0);

  return canvasToBlob(canvas);
}

function createCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement | OffscreenCanvas): Promise<Blob> {
  if (canvas instanceof OffscreenCanvas) {
    return canvas.convertToBlob({ type: 'image/png' });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not encode the capture as PNG.'));
    }, 'image/png');
  });
}

/** Downscale a PNG blob to a gallery thumbnail. */
export async function makeThumbnail(source: Blob, maxEdge = 320): Promise<Blob> {
  return downscaleToLongEdge(source, maxEdge);
}

/**
 * Downscale a PNG blob so its longest edge is at most `maxEdge`. Never upscales.
 *
 * Two callers with nothing else in common: the gallery thumbnail, and phase 6's export ceiling.
 * The arithmetic was the same in both, and a second copy of it is a second place for the aspect
 * ratio to be got wrong.
 */
export async function downscaleToLongEdge(source: Blob, maxEdge: number): Promise<Blob> {
  const bitmap = await createImageBitmap(source);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = createCanvas(width, height);
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    throw new Error('Could not acquire a 2D context to resize the capture.');
  }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  return canvasToBlob(canvas);
}
